import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Classable, Document, FileAsset, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { ChevronDownIconGlyph } from '@shapeable/icons';
import {
  CellLabel,
  ContentNode,
  DownloadButton,
  EntityLinkValue,
  EntityValue,
  SiteFooterLayout,
  SiteHeaderLayout,
  SiteHeaderProvider,
  useLang,
} from '@shapeable/ui';
import { DOING_CHALK, DOING_DEEP_BLUE, DOING_SIGNAL_BLUE, DOING_SUNSHINE, PRIMARY, PRIMARY_DARK, PRIMARY_LIGHT } from '../../theme';

const cls = classNames('document-layout');

const heroGlow = keyframes`
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.55;
  }

  50% {
    transform: translate(-50%, -52%) scale(1.08);
    opacity: 0.75;
  }
`;

const cardRise = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export type DocumentLayoutProps = Classable & HasChildren & {
  entity: Document;
};

export const DocumentLayoutDefaultProps: Omit<DocumentLayoutProps, 'entity'> = {
};

type DownloadsDockProps = {
  isOpen: boolean;
};

type DownloadsListProps = {
  isOpen: boolean;
};

type DownloadsChevronProps = {
  isOpen: boolean;
};

const PageStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: ${DOING_CHALK};
  `,
});

const HeroStyles = breakpoints({
  base: css`
    position: relative;
    overflow: hidden;
    padding: ${theme.UNIT(16)} ${theme.UNIT(6)} ${theme.UNIT(12)};
    color: ${theme.COLOR('light')};
    background: ${theme.COLOR('primary')};
      
  `,
  tablet: css`
    padding: ${theme.UNIT(20)} ${theme.UNIT(10)} ${theme.UNIT(14)};
  `,
  desktop: css`
    padding: ${theme.UNIT(24)} ${theme.UNIT(15)} ${theme.UNIT(16)};
  `,
});

const HeroInnerStyles = breakpoints({
  base: css`
    position: relative;
    z-index: 1;
    max-width: 56rem;
    margin: 0 auto;
    display: grid;
    gap: ${theme.UNIT(5)};
  `,
});

const HeroEyebrowStyles = breakpoints({
  base: css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.UNIT(2)};
    width: fit-content;
    padding: ${theme.UNIT(1.5)} ${theme.UNIT(3)};
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${theme.COLOR('dark')};
    background: ${theme.COLOR(DOING_SUNSHINE)};
  `,
});

const HeroTitleStyles = breakpoints({
  base: css`
    .shp--entity-value__title {
      color: ${theme.COLOR('light')};
      font-size: clamp(2rem, 6vw, 3.8rem);
      line-height: 1.05;
      letter-spacing: -0.03em;
    }

    .shp--badge {
      margin-top: ${theme.UNIT(3)};
      padding: ${theme.UNIT(1)} ${theme.UNIT(2.5)};
      color: ${DOING_DEEP_BLUE};
      background: rgba(255, 255, 255, 0.88);
      border: 1px solid rgba(255, 255, 255, 0.35);
    }
  `,
});

const MetaGridStyles = breakpoints({
  base: css`
    display: grid;
    gap: ${theme.UNIT(4)};
    grid-template-columns: 1fr;
  `,
  tablet: css`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `,
});

const MetaItemStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(1.5)};
    padding: ${theme.UNIT(4)};
    border-radius: ${theme.UNIT(4)};
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(8px);
  `,
});

const LabelStyles = breakpoints({
  base: css`
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  `,
});

const BodyStyles = breakpoints({
  base: css`
    flex: 1;
    padding: 0 ${theme.UNIT(3)} ${theme.UNIT(10)};
    margin-top: calc(-1 * ${theme.UNIT(8)});
  `,
  tablet: css`
    padding: 0 ${theme.UNIT(10)} ${theme.UNIT(12)};
  `,
  desktop: css`
    padding: 0 ${theme.UNIT(15)} ${theme.UNIT(14)};
  `,
});

const PanelStyles = breakpoints({
  base: css`
    max-width: 56rem;
    margin: 0 auto;
    padding: ${theme.UNIT(8)} ${theme.UNIT(6)};
    border-radius: ${theme.UNIT(6)};
    background: ${theme.COLOR('light')};
    box-shadow: 0 24px 60px rgba(15, 36, 80, 0.12);
    animation: ${cardRise} 700ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
  `,
  tablet: css`
    padding: ${theme.UNIT(10)} ${theme.UNIT(8)};
  `,
});

const DownloadsDockStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    margin-bottom: ${theme.UNIT(8)};
    border-radius: ${theme.UNIT(4)};
    background: ${DOING_CHALK};
    box-shadow: inset 0 0 0 1px ${PRIMARY}22;
  `,
});

const DownloadsToggleStyles = breakpoints({
  base: css`
    ${theme.REMOVE_BUTTON_STYLE()};
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.UNIT(2)};
    width: 100%;
    padding: ${theme.UNIT(4)} ${theme.UNIT(4)};
    font-weight: 700;
    color: ${theme.COLOR('light')};
    background: ${theme.COLOR('primary')};
  `,
});

const DownloadsChevronStyles = breakpoints({
  base: css`
    display: inline-flex;
    transform: rotate(${({ isOpen }: DownloadsChevronProps) => (isOpen ? '180deg' : '0deg')});
    transition: transform 180ms ease;
  `,
});

const DownloadsListStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)};
    overflow: hidden;
    max-height: 0;
    opacity: 0;
    padding: 0 ${theme.UNIT(3)};
    pointer-events: none;
    transition: opacity 0.22s ease, padding 0.3s ease, max-height 0.3s ease;

    ${({ isOpen }: DownloadsListProps) => isOpen && css`
      max-height: ${theme.UNIT(120)};
      opacity: 1;
      padding: ${theme.UNIT(3)};
      pointer-events: auto;
    `}
  `,
});

const ContentStyles = breakpoints({
  base: css`
    color: ${DOING_DEEP_BLUE};

    h1, h2, h3 {
      color: ${PRIMARY};
    }

    h1 {
      font-size: 2rem;
      line-height: 1.1;
    }

    h2 {
      font-size: 1.6rem;
      line-height: 1.15;
    }

    p {
      font-size: 1.05rem;
      line-height: 1.7;
    }
  `,
});

const FooterStyles = breakpoints({
  base: css`
    margin-top: auto;
  `,
});

const IconAndtitleStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('light')};
    ${theme.FILL('light')};
  `,
});

const OwnerStyles = breakpoints({
  base: css`
    .shp--entity-value__name {
      color: ${theme.COLOR('light')};
    }
    ${theme.FILL('light')};
  `,
});

const DownloadButtonStyles = breakpoints({
  base: css`
    max-width: fit-content;

    .shp--button {
      text-transform: none;
    }
  `,
});

const My = {
  Container: styled.div`${PageStyles}`,
    Header: styled(SiteHeaderLayout)``,
    Hero: styled.section`${HeroStyles}`,
      HeroInner: styled.div`${HeroInnerStyles}`,
      HeroEyebrow: styled.div`${HeroEyebrowStyles}`,
      HeroTitle: styled.div`${HeroTitleStyles}`,
    MetaGrid: styled.div`${MetaGridStyles}`,
      MetaItem: styled.div`${MetaItemStyles}`,
      Label: styled(CellLabel)`${LabelStyles}`,
      IconAndtitle: styled(EntityValue)`${IconAndtitleStyles}`,
      Owner: styled(EntityLinkValue)`${OwnerStyles}`,
    Body: styled.main`${BodyStyles}`,
      Panel: styled.div`${PanelStyles}`,
      DownloadsDock: styled.div<DownloadsDockProps>`${DownloadsDockStyles}`,
      DownloadsToggle: styled.button`${DownloadsToggleStyles}`,
      DownloadsChevron: styled.span<DownloadsChevronProps>`${DownloadsChevronStyles}`,
      DownloadsList: styled.div<DownloadsListProps>`${DownloadsListStyles}`,
      Content: styled(ContentNode)`${ContentStyles}`,
      DownloadButton: styled(DownloadButton)`${DownloadButtonStyles}`,
    Footer: styled(SiteFooterLayout)`${FooterStyles}`,
};

export const DocumentLayout: Shapeable.FC<DocumentLayoutProps> = (props) => {
  const { className, entity } = props;
  const t = useLang();
  const [showDownloads, setShowDownloads] = React.useState(true);

  const { content, files = [], folder, owner, type } = entity || {};
  const hasContent = !!content?.text;
  const hasFiles = files.length > 0;

  return (
    <SiteHeaderProvider value={{ variant: 'default', boundary: 'content' }}>
      <My.Container className={cls.name(className)}>
        <My.Header />
        <My.Hero>
          <My.HeroInner>
            <My.HeroEyebrow>
              {type?.name || t('Document')}
            </My.HeroEyebrow>
            <My.HeroTitle>
              <My.IconAndtitle showLabel={false} entity={entity} variant="banner" badge={folder?.name} />
            </My.HeroTitle>
            <My.MetaGrid>
              {!!folder && (
                <My.MetaItem>
                  <My.Label>{t('Folder')}</My.Label>
                  <strong>{folder.name}</strong>
                </My.MetaItem>
              )}
              {!!owner && (
                <My.MetaItem>
                  <My.Label>{t('Owned by')}</My.Label>
                  <My.Owner entity={owner} showLabel={false} />
                </My.MetaItem>
              )}
            </My.MetaGrid>
          </My.HeroInner>
        </My.Hero>
        <My.Body>
          <My.Panel>
            {hasFiles && (
              <My.DownloadsDock isOpen={showDownloads}>
                <My.DownloadsToggle
                  type="button"
                  onClick={() => setShowDownloads(!showDownloads)}
                >
                  {t('File downloads')} ({files.length})
                  <My.DownloadsChevron isOpen={showDownloads}>
                    <ChevronDownIconGlyph size={16} colors={{ fill: 'canvas' }} />
                  </My.DownloadsChevron>
                </My.DownloadsToggle>
                <My.DownloadsList isOpen={showDownloads}>
                  {files.map((file: FileAsset) => {
                    const rawUrl = file?.file?.url || file?.url;
                    const ext = file?.file?.extension;

                    if (!rawUrl) {
                      return null;
                    }

                    const downloadUrl = rawUrl.replace(/upload\//, 'upload/fl_attachment/');

                    return (
                      <My.DownloadButton
                        key={file.id}
                        href={downloadUrl}
                        colors={{
                          text: 'primary',
                          fill: 'primary',
                          border: 'primary',
                        }}
                      >
                        {file?.name} {ext && `(${ext})`}
                      </My.DownloadButton>
                    );
                  })}
                </My.DownloadsList>
              </My.DownloadsDock>
            )}
            {hasContent && <My.Content entity={content} />}
          </My.Panel>
        </My.Body>
      </My.Container>
    </SiteHeaderProvider>
  );
};

DocumentLayout.defaultProps = DocumentLayoutDefaultProps;
DocumentLayout.cls = cls;
